import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { githubApi } from '@/services/githubApi';
import { Spinner } from '@systemdox/ui';
import { redeemGitHubCallback } from './githubCallback';

// The parse/redeem logic lives in ./githubCallback (pure, tested there).
export { parseGitHubCallback, redeemGitHubCallback } from './githubCallback';

/**
 * Completes a GitHub App installation. Mounted on BOTH hosts — the tenant
 * root, and the neutral app.systemdox.com that GitHub's fixed callback URL
 * points at (#885) — because the API resolves the tenant from the token,
 * not the origin.
 */
export default function GitHubCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const flight = useRef({ redeemed: false });

  useEffect(() => {
    redeemGitHubCallback(searchParams, flight.current, {
      redeem: (code, installationId, state) => githubApi.oauthCallback(code, installationId, state),
      onSuccess: () => navigate('/settings/github', { replace: true }),
      onError: setError,
    });
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="p-8 max-w-lg">
        <h1 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">
          GitHub Connection Failed
        </h1>
        <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">{error}</p>
        <a
          href="/settings/github"
          className="text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          Back to GitHub Settings
        </a>
      </div>
    );
  }

  return (
    <div className="p-8 flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
      <Spinner size="sm" />
      Connecting GitHub account...
    </div>
  );
}
